import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { Play, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { Episode } from '@/hooks/useEpisodes';

interface EpisodeCardProps {
  episode: Episode;
}

export function EpisodeCard({ episode }: EpisodeCardProps) {
  const noteId = nip19.noteEncode(episode.event.id);

  const formattedDate = new Date(episode.event.created_at * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  // Guest label from hashtags (first non-simplynostr hashtag)
  const guestTag = episode.hashtags.find(
    t => !['simplynostr', 'simpynostr'].includes(t.toLowerCase()) && !/^\d+$/.test(t)
  );

  return (
    <Link to={`/${noteId}`} className="block group">
      <Card className="overflow-hidden border border-border/50 bg-card hover:border-purple-500/50 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-300 h-full">
        {/* Thumbnail / Video Preview */}
        <div className="relative aspect-video bg-gradient-to-br from-purple-950 to-black overflow-hidden">
          {episode.thumbnailUrl ? (
            <img
              src={episode.thumbnailUrl}
              alt={episode.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="flex flex-col items-center gap-3 opacity-40">
                <div className="w-16 h-16 rounded-full border-2 border-purple-400 flex items-center justify-center">
                  <Play className="w-7 h-7 text-purple-400 ml-1" />
                </div>
                <span className="text-purple-400 text-sm font-medium tracking-wider uppercase">Simply Nostr</span>
              </div>
            </div>
          )}

          {/* Play overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-purple-600/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300 shadow-lg shadow-purple-500/50">
              <Play className="w-6 h-6 text-white ml-1" />
            </div>
          </div>

          {/* Episode number badge */}
          {episode.episodeNumber && (
            <div className="absolute top-3 left-3">
              <Badge className="bg-purple-600/90 text-white border-0 text-xs font-semibold">
                Ep. {episode.episodeNumber}
              </Badge>
            </div>
          )}


        </div>

        <CardContent className="p-4 space-y-2">
          {/* Guest tag */}
          {guestTag && (
            <p className="text-xs font-semibold text-purple-400 uppercase tracking-widest">
              #{guestTag}
            </p>
          )}

          {/* Title */}
          <h3 className="font-semibold text-foreground leading-snug line-clamp-2 group-hover:text-purple-300 transition-colors text-base">
            {episode.title}
          </h3>

          {/* Description */}
          {episode.description && (
            <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
              {episode.description}
            </p>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between pt-1">
            <span className="text-xs text-muted-foreground">{formattedDate}</span>
            {episode.videoUrl && (
              <a
                href={episode.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300 transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                Watch
              </a>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

// Skeleton loader for EpisodeCard
export function EpisodeCardSkeleton() {
  return (
    <Card className="overflow-hidden border border-border/50 h-full">
      <div className="aspect-video bg-muted animate-pulse" />
      <CardContent className="p-4 space-y-3">
        <div className="h-3 w-20 bg-muted rounded animate-pulse" />
        <div className="space-y-2">
          <div className="h-4 w-full bg-muted rounded animate-pulse" />
          <div className="h-4 w-3/4 bg-muted rounded animate-pulse" />
        </div>
        <div className="h-3 w-full bg-muted rounded animate-pulse" />
        <div className="h-3 w-5/6 bg-muted rounded animate-pulse" />
        <div className="flex justify-between pt-1">
          <div className="h-3 w-16 bg-muted rounded animate-pulse" />
          <div className="h-3 w-12 bg-muted rounded animate-pulse" />
        </div>
      </CardContent>
    </Card>
  );
}
